/*
 * Kubernetes Overview Card Component
 * Glassmorphism + Monospace Style
 */

import React from 'react';
import { Box, Typography, Card, CardHeader, CardContent } from '@material-ui/core';
import { useKubernetesEnhancedStyles, colors } from '../styles/theme';
import { ResourceOverview } from '../types';

interface KubernetesOverviewCardProps {
  overview: ResourceOverview;
  loading?: boolean;
}

interface OverviewItemProps {
  label: string;
  value: number | string;
  subValue?: string;
  status?: 'success' | 'warning' | 'error' | 'info';
}

const OverviewItem: React.FC<OverviewItemProps> = ({ label, value, subValue, status }) => {
  const classes = useKubernetesEnhancedStyles();
  
  const getGradient = () => {
    switch (status) {
      case 'success': return colors.gradientSuccess;
      case 'warning': return colors.gradientWarning;
      case 'error': return colors.gradientError;
      case 'info': return colors.gradientCyan;
      default: return colors.gradientPrimary;
    }
  };
  
  return (
    <Box className={classes.overviewItem}>
      <Typography className={classes.overviewLabel}>{label}</Typography>
      <Typography 
        className={classes.overviewValue} 
        style={{ 
          background: getGradient(),
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        {value}
      </Typography>
      {subValue && (
        <Typography className={classes.overviewStatus}>{subValue}</Typography>
      )}
    </Box>
  );
};

export const KubernetesOverviewCard: React.FC<KubernetesOverviewCardProps> = ({
  overview,
}) => {
  const classes = useKubernetesEnhancedStyles();
  
  const { pods, services, ingresses, configMaps } = overview;
  
  // 计算 Pod 状态
  const podStatus = pods.running === pods.total && pods.total > 0 ? 'success' : 
                    pods.failed > 0 ? 'error' : 
                    pods.pending > 0 ? 'warning' : 'info';
  
  const podSubValue = pods.total > 0 
    ? `${pods.running} running` 
    : 'no pods';
  
  return (
    <Card className={classes.card}>
      <CardHeader
        title="// RESOURCE_OVERVIEW"
        className={classes.cardHeader}
      />
      <CardContent className={classes.cardContent}>
        <Box className={classes.overviewGrid}>
          <OverviewItem
            label="PODS"
            value={`${pods.running}/${pods.total}`}
            subValue={podSubValue}
            status={podStatus}
          />
          <OverviewItem
            label="SERVICES"
            value={services}
            subValue={services > 0 ? 'active' : 'none'}
            status={services > 0 ? 'info' : undefined}
          />
          <OverviewItem
            label="INGRESSES"
            value={ingresses}
            subValue={ingresses > 0 ? 'active' : 'none'}
            status={ingresses > 0 ? 'info' : undefined}
          />
          <OverviewItem
            label="CONFIGMAPS"
            value={configMaps}
            subValue={configMaps > 0 ? 'loaded' : 'none'}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
